import { createContext, useContext, useEffect, useMemo, useState } from 'react';

const KONAMI_SEQUENCE = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];
const TRUE_VALUES = new Set(['true', '1', 'yes', 'on']);

const SpecialFeatureUnlockContext = createContext({
  isSpecialFeatureEnabled: false,
  isKonamiUnlocked: false,
});

function parseSpecialFeatureFlag() {
  const env = typeof process !== 'undefined' ? process.env : {};
  const rawValue = env.REACT_APP_SPECIAL_FEATURE ?? env.SPECIAL_FEATURE ?? 'false';
  return TRUE_VALUES.has(String(rawValue).trim().toLowerCase());
}

export function SpecialFeatureUnlockProvider({ children }) {
  const isSpecialFeatureEnabled = parseSpecialFeatureFlag();
  const [isKonamiUnlocked, setIsKonamiUnlocked] = useState(false);

  useEffect(() => {
    if (!isSpecialFeatureEnabled) return undefined;

    let sequenceIndex = 0;

    const onKeyDown = (event) => {
      if (!event.key) return;
      const normalizedKey = event.key.length === 1 ? event.key.toLowerCase() : event.key;
      const expectedKey = KONAMI_SEQUENCE[sequenceIndex];

      if (normalizedKey === expectedKey) {
        sequenceIndex += 1;
        if (sequenceIndex === KONAMI_SEQUENCE.length) {
          setIsKonamiUnlocked(true);
          sequenceIndex = 0;
        }
        return;
      }

      sequenceIndex = normalizedKey === KONAMI_SEQUENCE[0] ? 1 : 0;
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isSpecialFeatureEnabled]);

  const contextValue = useMemo(
    () => ({
      isSpecialFeatureEnabled,
      isKonamiUnlocked,
    }),
    [isSpecialFeatureEnabled, isKonamiUnlocked]
  );

  return <SpecialFeatureUnlockContext.Provider value={contextValue}>{children}</SpecialFeatureUnlockContext.Provider>;
}

export function useSpecialFeatureUnlock() {
  return useContext(SpecialFeatureUnlockContext);
}
